import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Alert,
  Divider,
  Paper,
  Grid,
  Stack,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { ConceptSelector } from './ConceptSelector';
import { ConceptOption } from '../../types';

interface Factura {
  id: string; // DataGrid ID (for MUI DataGrid)
  ID: number; // Database ID (unique primary key)
  NIT: string;
  'PROVEEDOR': string;
  'TIPO DOC.': string;
  'CONCEPTO': string;
  'FECHA': string;
  'No. DOC.': string;
  'V/R COBRO': number;
  'Concepto Aprobacion': string;
  'Lote': string;
  'Tipo Lote': string;
  'CP'?: string;
}

interface SplitRow {
  id: string;
  conceptoAprobacion: string;
  vrCobro: number;
  isEditable: boolean;
  isOriginal: boolean;
}

interface SplitData {
  originalRecordId: string;
  splits: SplitRow[];
  remainingValue: number;
}

interface ModalState {
  isOpen: boolean;
  isSubmitting: boolean;
  hasUnsavedChanges: boolean;
}

interface SplitInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  originalRecord: Factura | null;
  onConfirm: (splitData: SplitData) => Promise<void>;
  modalState?: ModalState;
  onModalStateChange?: (updates: Partial<ModalState>) => void;
}

export const SplitInvoiceModal: React.FC<SplitInvoiceModalProps> = ({
  open,
  onClose,
  originalRecord,
  onConfirm,
  modalState,
  onModalStateChange
}) => {
  const [splitRows, setSplitRows] = useState<SplitRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // Use external modal state if provided, otherwise use local state
  const isSubmitting = modalState?.isSubmitting ?? false;
  const hasUnsavedChanges = modalState?.hasUnsavedChanges ?? false;

  // Initialize split rows when modal opens
  useEffect(() => {
    if (open && originalRecord) {
      const initialRow: SplitRow = {
        id: 'original',
        conceptoAprobacion: originalRecord['Concepto Aprobacion'] || '',
        vrCobro: originalRecord['V/R COBRO'] || 0,
        isEditable: false,
        isOriginal: true
      };
      setSplitRows([initialRow]);
      setError(null);
      onModalStateChange?.({ hasUnsavedChanges: false, isSubmitting: false });
    }
  }, [open, originalRecord]);

  // Calculate totals and remaining value
  const calculations = useMemo(() => {
    const originalValue = originalRecord?.['V/R COBRO'] || 0;
    const totalSplitValue = splitRows
      .filter(row => !row.isOriginal)
      .reduce((sum, row) => sum + (row.vrCobro || 0), 0);
    const remainingValue = originalValue - totalSplitValue;
    
    return {
      originalValue,
      totalSplitValue,
      remainingValue,
      isValid: remainingValue >= 0 && Math.abs(remainingValue - (splitRows.find(r => r.isOriginal)?.vrCobro || 0)) < 0.01
    };
  }, [splitRows, originalRecord]);

  // Update original row value when splits change
  useEffect(() => {
    setSplitRows(prevRows => 
      prevRows.map(row => 
        row.isOriginal 
          ? { ...row, vrCobro: calculations.remainingValue }
          : row
      )
    );
  }, [calculations.remainingValue]);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleAddSplitRow = () => {
    const newRow: SplitRow = {
      id: `split_${Date.now()}`,
      conceptoAprobacion: '',
      vrCobro: 0,
      isEditable: true,
      isOriginal: false
    };
    setSplitRows(prev => [...prev, newRow]);
    onModalStateChange?.({ hasUnsavedChanges: true });
  };

  const handleRemoveSplitRow = (rowId: string) => {
    setSplitRows(prev => prev.filter(row => row.id !== rowId));
    onModalStateChange?.({ hasUnsavedChanges: true });
  };

  const handleSplitRowChange = (rowId: string, field: keyof SplitRow, value: any) => {
    setSplitRows(prev => 
      prev.map(row => 
        row.id === rowId 
          ? { ...row, [field]: value }
          : row
      )
    );
    onModalStateChange?.({ hasUnsavedChanges: true });
  };

  const validateForm = (): string | null => {
    // Check if there are any split rows
    const editableRows = splitRows.filter(row => !row.isOriginal);
    if (editableRows.length === 0) {
      return 'Debe agregar al menos una subdivisión';
    }

    // Concepto selection is now optional - users can leave it blank

    // Check if all editable rows have valid values
    const rowsWithInvalidValues = editableRows.filter(row => row.vrCobro <= 0);
    if (rowsWithInvalidValues.length > 0) {
      return 'Todos los valores de subdivisión deben ser mayores a cero';
    }

    // Check if remaining value is valid
    if (calculations.remainingValue < 0) {
      return 'La suma de las subdivisiones no puede exceder el valor original';
    }

    // Check if remaining value for original row is valid
    if (calculations.remainingValue <= 0) {
      return 'Debe quedar un valor positivo para el registro original';
    }

    return null;
  };

  const handleConfirm = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!originalRecord) {
      setError('No se encontró el registro original');
      return;
    }

    onModalStateChange?.({ isSubmitting: true });
    setError(null);

    try {
      const splitData: SplitData = {
        originalRecordId: originalRecord.id,
        splits: splitRows,
        remainingValue: calculations.remainingValue
      };

      await onConfirm(splitData);
      // Modal will be closed by parent component after successful operation
    } catch (error) {
      console.error('Error confirming split:', error);
      setError(error instanceof Error ? error.message : 'Error al procesar la subdivisión');
    } finally {
      onModalStateChange?.({ isSubmitting: false });
    }
  };

  const handleClose = () => {
    // Let parent component handle the close logic including unsaved changes check
    setSplitRows([]);
    setError(null);
    onClose();
  };

  if (!originalRecord) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Subdividir Factura
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Original Record Information */}
        <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom color="primary">
            Información del Registro Original
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Proveedor
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {originalRecord['PROVEEDOR']}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                No. Documento
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {originalRecord['No. DOC.']}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Concepto Original
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {originalRecord['CONCEPTO']}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Valor Total
              </Typography>
              <Typography variant="body1" fontWeight="medium" color="primary">
                {formatCurrency(originalRecord['V/R COBRO'])}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} icon={<WarningIcon />}>
            {error}
          </Alert>
        )}

        {/* Split Rows */}
        <Box sx={{ mb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle2" color="primary">
              Subdivisiones
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddSplitRow}
              disabled={isSubmitting}
            >
              Subdividir
            </Button>
          </Box>

          <Stack spacing={2}>
            {splitRows.map((row, index) => (
              <Paper
                key={row.id}
                elevation={1}
                sx={{
                  p: 2,
                  backgroundColor: row.isOriginal ? 'primary.50' : 'background.paper',
                  border: row.isOriginal ? '1px solid' : 'none',
                  borderColor: row.isOriginal ? 'primary.200' : 'transparent'
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Chip
                    label={row.isOriginal ? 'Original' : `Subdivisión ${index}`}
                    size="small"
                    color={row.isOriginal ? 'primary' : 'default'}
                    variant={row.isOriginal ? 'filled' : 'outlined'}
                  />
                  
                  <Box flex={1}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <ConceptSelector
                          value={row.conceptoAprobacion}
                          onChange={(value) => handleSplitRowChange(row.id, 'conceptoAprobacion', value)}
                          disabled={!row.isEditable || isSubmitting}
                          placeholder={row.isOriginal ? "Concepto original" : "Seleccionar concepto..."}
                          size="small"
                          error={false}
                          helperText={""}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          type="number"
                          value={row.vrCobro}
                          onChange={(e) => handleSplitRowChange(row.id, 'vrCobro', parseFloat(e.target.value) || 0)}
                          disabled={!row.isEditable || isSubmitting}
                          size="small"
                          fullWidth
                          label="Valor"
                          InputProps={{
                            inputProps: { min: 0, step: 0.01 }
                          }}
                          error={row.isEditable && row.vrCobro <= 0}
                          helperText={row.isEditable && row.vrCobro <= 0 ? "Debe ser mayor a cero" : ""}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(row.vrCobro)}
                          </Typography>
                          {row.isEditable && (
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveSplitRow(row.id)}
                              disabled={isSubmitting}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Summary */}
        <Paper elevation={1} sx={{ p: 2, backgroundColor: 'info.50' }}>
          <Typography variant="subtitle2" gutterBottom color="info.main">
            Resumen de Valores
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Valor Original:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatCurrency(calculations.originalValue)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Total Subdivisiones:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatCurrency(calculations.totalSplitValue)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Valor Restante:
              </Typography>
              <Typography 
                variant="body1" 
                fontWeight="medium"
                color={calculations.remainingValue < 0 ? 'error.main' : 'success.main'}
              >
                {formatCurrency(calculations.remainingValue)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Estado:
              </Typography>
              <Chip
                label={calculations.isValid ? 'Válido' : 'Inválido'}
                size="small"
                color={calculations.isValid ? 'success' : 'error'}
                variant="filled"
              />
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          color="inherit"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isSubmitting || !calculations.isValid || splitRows.filter(r => !r.isOriginal).length === 0}
          color="primary"
        >
          {isSubmitting ? 'Procesando...' : 'Aceptar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SplitInvoiceModal;